import * as React from 'react';

import type { HiveWorkingDocument } from '@gitbook/hive-client';
import type { Change, SelectionRange, Value } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

/**
 * Subscribe to the value and selections of a HiveWorkingDocument.
 */
export function useHiveWorkingDocumentValue(
    page: HiveWorkingDocument
): [Value, List<SelectionRange>, (change: Change) => void] {
    const listeners = React.useMemo(
        () => ({
            value: {
                subscribe: (callback: () => void) => page.listen('value', callback),
                getSnapshot: () => page.value,
            },
            selections: {
                subscribe: (callback: () => void) => page.listen('selections', callback),
                getSnapshot: () => page.selections,
            },
        }),
        [page]
    );

    const value = React.useSyncExternalStore(
        listeners.value.subscribe,
        listeners.value.getSnapshot
    );

    const selections = React.useSyncExternalStore(
        listeners.selections.subscribe,
        listeners.selections.getSnapshot
    );

    const onChange = React.useCallback(
        (change: Change) => {
            page.applyChange(change);
        },
        [page]
    );

    return [value, selections, onChange];
}
